import {
  type MessageResponseMap,
  type PerfectPostMessage,
  type PerfectPostMessageResponse,
  isPerfectPostMessageResponse,
} from '@perfectpost/perfect-post-common';
import Semaphore from 'semaphore-promise';

import {TimeoutException} from './TimeoutException';

const DEFAULT_EXTENSION_TIMEOUT_MS = 10_000;

const genId = () =>
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'req_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

type WithReqId<T> = T & {requestId?: string};

// Cible de messagerie minimale : par défaut `window`, mais injectable pour tester sans navigateur.
export interface ExtensionMessageTarget {
  readonly origin: string;
  postMessage(message: unknown, targetOrigin: string): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

// Transport de l'extension : request/response corrélé (reqId + timeout) + abonnement aux push.
// Seul point réellement couplé au navigateur (via la cible injectée).
export interface ExtensionTransport {
  request<T extends PerfectPostMessage['type']>(
    message: Extract<PerfectPostMessage, {type: T}>,
  ): Promise<MessageResponseMap[T]>;
  subscribe(handler: (event: MessageEvent) => void): () => void;
  dispose(): void;
}

const windowTarget = (): ExtensionMessageTarget => ({
  origin: window.location.origin,
  postMessage: (message, targetOrigin) => window.postMessage(message, targetOrigin),
  addEventListener: (type, listener) => window.addEventListener(type, listener),
  removeEventListener: (type, listener) => window.removeEventListener(type, listener),
});

export function createExtensionTransport(
  target: ExtensionMessageTarget = windowTarget(),
  {timeoutMs = DEFAULT_EXTENSION_TIMEOUT_MS}: {timeoutMs?: number} = {},
): ExtensionTransport {
  const pendingById = new Map<
    string,
    {resolve: (value: PerfectPostMessageResponse) => void; reject: (reason?: unknown) => void}
  >();
  const subscribers = new Set<(event: MessageEvent) => void>();
  // Sérialise les envois (une requête en vol à la fois), comme l'ancien messenger.
  const semaphore = new Semaphore(1);

  // Réponses sans requestId (legacy) : comme une seule requête est en vol, on route vers elle.
  const singlePending = () => (pendingById.size === 1 ? [...pendingById.values()][0] : undefined);

  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== target.origin) return;

    subscribers.forEach((handler) => handler(event));

    if (!isPerfectPostMessageResponse(event.data)) return;
    const response = event.data as WithReqId<PerfectPostMessageResponse>;

    const isError =
      response.type === 'error_response' ||
      (response.type === 'resolve_profile_mention_response' && response.status === 'error');

    if (isError) {
      const message =
        response.type === 'error_response'
          ? response.payload.error
          : typeof response.error === 'string'
            ? response.error
            : 'resolve_profile_mention failed';
      // Un requestId connu route précisément ; un requestId absent (réponse legacy) retombe sur
      // l'unique requête en vol. Un requestId présent mais INCONNU est ignoré : c'est presque
      // toujours une réponse tardive (après timeout) qui contaminerait une autre requête en vol.
      const pending = response.requestId !== undefined ? pendingById.get(response.requestId) : singlePending();
      pending?.reject(new Error(message));
      return;
    }

    const pending = response.requestId !== undefined ? pendingById.get(response.requestId) : singlePending();
    pending?.resolve(response);
  };

  target.addEventListener('message', handleMessage);

  const request = async <T extends PerfectPostMessage['type']>(
    message: Extract<PerfectPostMessage, {type: T}>,
  ): Promise<MessageResponseMap[T]> => {
    const release = await semaphore.acquire();
    const requestId = genId();
    (message as WithReqId<PerfectPostMessage>).requestId = requestId;

    try {
      return (await new Promise<PerfectPostMessageResponse>((resolve, reject) => {
        const timeout = setTimeout(() => {
          pendingById.delete(requestId);
          reject(new TimeoutException('Extension did not respond in time'));
        }, timeoutMs);

        pendingById.set(requestId, {
          resolve: (value) => {
            clearTimeout(timeout);
            pendingById.delete(requestId);
            resolve(value);
          },
          reject: (reason) => {
            clearTimeout(timeout);
            pendingById.delete(requestId);
            reject(reason);
          },
        });

        target.postMessage(message, target.origin);
      })) as MessageResponseMap[T];
    } finally {
      release();
    }
  };

  return {
    request,
    subscribe: (handler) => {
      subscribers.add(handler);
      return () => {
        subscribers.delete(handler);
      };
    },
    dispose: () => {
      target.removeEventListener('message', handleMessage);
      subscribers.clear();
      pendingById.clear();
    },
  };
}
